import { CustomizerPanelController } from '@theme/customizer-panel-controller';

/**
 * Marketing LP customizer launcher.
 *
 * Invisible host rendered once per page by sections/lp-customizer-launcher.liquid.
 * Intercepts clicks on stock product-card links anywhere on the page and opens
 * the shared slide-over customizer panel (loaded via the Section Rendering API)
 * instead of navigating to the product page.
 *
 * Listens in the document capture phase so it runs before product-card's own
 * target-phase handlers (section-level listeners can't preempt them otherwise).
 * Modifier-clicks and middle-clicks fall through so "open in new tab" still
 * reaches the product page. Cards inside the cart drawer, dialogs, or the
 * panel itself are ignored.
 */
class LpCustomizerLauncherComponent extends HTMLElement {
  #controller = null;
  #clickHandler = null;

  connectedCallback() {
    this.#controller = new CustomizerPanelController(this);
    this.#controller.connect();

    this.#clickHandler = (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;

      const link = event.target.closest('a[href]');
      if (!link) return;
      if (!link.closest('product-card')) return;
      if (link.closest('dialog, [data-personalized-panel], cart-drawer-component')) return;

      const url = link.getAttribute('href');
      if (!url || !url.includes('/products/')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.#controller.open(url, link);
    };
    document.addEventListener('click', this.#clickHandler, true);
  }

  disconnectedCallback() {
    if (this.#clickHandler) document.removeEventListener('click', this.#clickHandler, true);
    this.#controller?.disconnect();
    this.#controller = null;
  }
}

if (!customElements.get('lp-customizer-launcher-component')) {
  customElements.define('lp-customizer-launcher-component', LpCustomizerLauncherComponent);
}

// --- Shopify theme editor lifecycle ---
// Close the panel when the section is unloaded by the editor.
if (typeof document !== 'undefined') {
  document.addEventListener('shopify:section:unload', (event) => {
    const sectionId = event.detail?.sectionId;
    if (!sectionId) return;
    const root = document.querySelector(
      `lp-customizer-launcher-component[data-section-id="${sectionId}"]`
    );
    root?.querySelector('[data-personalized-panel-close]')?.click();
  });
}
